import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowSquareOut, Lightning, FileText, Envelope, ChatText, MapPin, Buildings, Calendar, Briefcase, MagnifyingGlass, User, Globe, Warning, RocketLaunch, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function JobDrawer() {
  const { selectedJob, updateJobStatus, scoreJob, generateContent, loading, researchCompany, findHiringManagers } = useApp();
  const [research, setResearch] = useState(null);
  const [contacts, setContacts] = useState(null);
  const job = selectedJob;
  if (!job) return null;

  const scoreColor = job.match_score >= 75 ? "#10B981" : job.match_score >= 50 ? "#F59E0B" : "#EF4444";

  const handleGenerate = async (type) => {
    try {
      const result = await generateContent(job.id, type);
      if (result?.content) toast.success(`Generated ${type.replace('_', ' ')}`);
    } catch (e) { toast.error("Generation failed"); }
  };

  const handleResearch = async () => {
    if (!job.company) { toast.error("No company name available"); return; }
    toast.info(`Researching ${job.company}... This may take 10-20 seconds.`);
    try {
      const result = await researchCompany(job.company);
      if (result?.error) {
        toast.error(`Research failed: ${result.error}`);
      } else {
        setResearch(result?.research || result);
        toast.success(`Research complete for ${job.company}`);
      }
    } catch (e) { toast.error("Research failed — check activity log"); }
  };

  const handleFindContacts = async () => {
    if (!job.company) { toast.error("No company name available"); return; }
    toast.info(`Searching for hiring contacts at ${job.company}...`);
    try {
      const result = await findHiringManagers(job.company, job.title);
      setContacts(result?.contacts || []);
      const count = (result?.contacts || []).length;
      if (count > 0) {
        toast.success(`Found ${count} contacts at ${job.company}`);
      } else {
        toast.info("No LinkedIn contacts found. AI generated likely contacts.");
      }
    } catch (e) { toast.error("Contact search failed — check activity log"); }
  };

  // ── One-Click Apply ──
  const handleApplyNow = async () => {
    if (job.job_url) window.open(job.job_url, '_blank');
    if (job.status !== 'applied') {
      await updateJobStatus(job.id, 'applied');
      toast.success("Applied! Job moved to Applied pipeline.", { icon: "🚀" });
    } else {
      toast.success("Application opened in new tab.");
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6" data-testid="job-drawer">
        <SheetHeader className="mb-5">
          <SheetTitle className="text-white text-lg font-semibold leading-tight font-['Outfit']">{job.title}</SheetTitle>
          <SheetDescription className="text-zinc-500 text-xs mt-1">AI-Powered Job Analysis</SheetDescription>
        </SheetHeader>

        {/* Company Info */}
        <div className="jp-card mb-4">
          <div className="grid grid-cols-2">
            <div className="p-4 border-r border-b border-white/5">
              <div className="jp-label mb-1"><Buildings size={12} className="inline mr-1" />COMPANY</div>
              <div className="text-sm text-white font-medium">{job.company || "N/A"}</div>
            </div>
            <div className="p-4 border-b border-white/5">
              <div className="jp-label mb-1"><MapPin size={12} className="inline mr-1" />LOCATION</div>
              <div className="text-sm text-white">{job.location || "N/A"}</div>
            </div>
            <div className="p-4 border-r border-white/5">
              <div className="jp-label mb-1"><Calendar size={12} className="inline mr-1" />POSTED</div>
              <div className="text-sm text-zinc-400">{job.date_posted || "N/A"}</div>
            </div>
            <div className="p-4">
              <div className="jp-label mb-1"><Briefcase size={12} className="inline mr-1" />SOURCE</div>
              <div className="flex items-center gap-2">
                <div className={`source-dot source-${(job.source || '').toLowerCase()}`} />
                <span className="text-sm text-zinc-400 uppercase">{job.source}</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Score */}
        <div className="jp-card mb-4">
          <div className="jp-card-header">
            <span className="jp-label">AI MATCH ANALYSIS</span>
            {!job.scored ? (
              <Button size="sm" data-testid="drawer-score-btn"
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-md text-xs h-7 px-3 font-medium"
                onClick={() => scoreJob(job.id)} disabled={loading[`score-${job.id}`]}>
                {loading[`score-${job.id}`] ? "..." : <><Lightning size={14} weight="fill" className="mr-1" />Score</>}
              </Button>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className="font-['Outfit'] text-3xl font-bold" style={{ color: scoreColor }}>{job.match_score}</span>
                <span className="text-xs text-zinc-600">/100</span>
              </div>
            )}
          </div>
          {job.scored && (
            <div className="p-5 space-y-4">
              {job.match_reasons?.length > 0 && (
                <div>
                  <div className="jp-label mb-2">MATCH REASONS</div>
                  {job.match_reasons.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1.5">
                      <span className="text-emerald-500 text-xs mt-0.5 font-mono">+</span>
                      <span className="text-xs text-zinc-400 leading-relaxed">{r}</span>
                    </div>
                  ))}
                </div>
              )}
              {job.missing_keywords?.length > 0 && (
                <div>
                  <div className="jp-label mb-2">GAPS</div>
                  <div className="flex flex-wrap gap-1.5">
                    {job.missing_keywords.map((k, i) => (<span key={i} className="jp-badge jp-badge-red">{k}</span>))}
                  </div>
                </div>
              )}
              {job.gap_analysis && (
                <div>
                  <div className="jp-label mb-2">ANALYSIS</div>
                  <p className="text-xs text-zinc-500 leading-relaxed">{job.gap_analysis}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status */}
        <div className="jp-card mb-4">
          <div className="px-5 py-3.5 flex items-center justify-between">
            <span className="jp-label">STATUS</span>
            <Select value={job.status} onValueChange={(val) => updateJobStatus(job.id, val)}>
              <SelectTrigger className="bg-black/40 border-white/10 rounded-md h-8 text-xs w-32" data-testid="drawer-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
                {["new","saved","applied","interview","offer","rejected"].map(s => (
                  <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Company Research (replaces Tavily) */}
        <div className="jp-card mb-4" data-testid="company-research-section">
          <div className="jp-card-header">
            <span className="jp-label">COMPANY INTEL</span>
            <Button size="sm" data-testid="research-company-btn"
              className="bg-transparent border border-white/10 text-zinc-400 hover:border-amber-500/40 hover:text-amber-500 rounded-md text-xs h-7 px-3"
              onClick={handleResearch} disabled={loading.research}>
              {loading.research ? <><SpinnerGap size={14} className="mr-1 animate-spin" />Researching...</> : <><Globe size={14} weight="duotone" className="mr-1" />Research</>}
            </Button>
          </div>
          {research && (
            <div className="p-5 space-y-3">
              {research.overview && (
                <div>
                  <div className="jp-label mb-1">OVERVIEW</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{research.overview}</p>
                </div>
              )}
              {research.culture && (
                <div>
                  <div className="jp-label mb-1">CULTURE</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{research.culture}</p>
                </div>
              )}
              {research.talking_points?.length > 0 && (
                <div>
                  <div className="jp-label mb-1">TALKING POINTS</div>
                  {research.talking_points.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1">
                      <span className="text-amber-500 text-xs mt-0.5">*</span>
                      <span className="text-xs text-zinc-400">{p}</span>
                    </div>
                  ))}
                </div>
              )}
              {research.red_flags?.length > 0 && (
                <div>
                  <div className="jp-label mb-1 flex items-center gap-1"><Warning size={12} className="text-red-400" />RED FLAGS</div>
                  {research.red_flags.map((f, i) => (
                    <div key={i} className="text-xs text-red-400/80 mb-1">- {f}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hiring Manager Finder (replaces Hunter.io) */}
        <div className="jp-card mb-4" data-testid="hiring-manager-section">
          <div className="jp-card-header">
            <span className="jp-label">HIRING CONTACTS</span>
            <Button size="sm" data-testid="find-contacts-btn"
              className="bg-transparent border border-white/10 text-zinc-400 hover:border-amber-500/40 hover:text-amber-500 rounded-md text-xs h-7 px-3"
              onClick={handleFindContacts} disabled={loading.contacts}>
              {loading.contacts ? <><SpinnerGap size={14} className="mr-1 animate-spin" />Searching...</> : <><MagnifyingGlass size={14} weight="duotone" className="mr-1" />Find Contacts</>}
            </Button>
          </div>
          {contacts && contacts.length > 0 && (
            <div className="p-4 space-y-2">
              {contacts.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                  <User size={16} className="text-zinc-600 shrink-0" weight="duotone" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-white font-medium truncate">{c.name}</div>
                    {c.role && <div className="text-[10px] text-zinc-600 truncate">{c.role}</div>}
                  </div>
                  {c.url && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-600 hover:text-amber-500 rounded-md shrink-0"
                      onClick={() => window.open(c.url, '_blank')}>
                      <ArrowSquareOut size={12} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {contacts && contacts.length === 0 && (
            <div className="p-4 text-xs text-zinc-600 text-center">No contacts found via DuckDuckGo dorking</div>
          )}
        </div>

        {/* AI Actions */}
        <div className="jp-card mb-4">
          <div className="jp-card-header"><span className="jp-label">AI GENERATE</span></div>
          <div className="p-4 space-y-2">
            {[
              { type: "cover_letter", label: "Cover Letter", icon: FileText },
              { type: "cold_email", label: "Cold Email", icon: Envelope },
              { type: "linkedin_message", label: "LinkedIn Message", icon: ChatText },
            ].map(a => (
              <Button key={a.type} data-testid={`gen-${a.type.replace('_','-')}-btn`}
                className="w-full bg-transparent border border-white/10 text-zinc-400 hover:border-amber-500/40 hover:text-amber-500 rounded-md text-xs h-9 justify-start font-medium"
                onClick={() => handleGenerate(a.type)} disabled={loading[`gen-${a.type}`]}>
                <a.icon size={15} className="mr-2" weight="duotone" />
                {loading[`gen-${a.type}`] ? "Generating..." : `Generate ${a.label}`}
              </Button>
            ))}
          </div>
        </div>

        {/* One-Click Apply */}
        {job.job_url && (
          <Button
            data-testid="apply-now-btn"
            className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-md text-sm h-10 mb-3 font-semibold"
            onClick={handleApplyNow}
          >
            <RocketLaunch size={16} weight="fill" className="mr-2" />
            Apply Now — Track in Pipeline
          </Button>
        )}

        {job.description && (
          <div className="jp-card">
            <div className="jp-card-header"><span className="jp-label">JOB DESCRIPTION</span></div>
            <div className="p-5 text-xs text-zinc-500 leading-relaxed max-h-[400px] overflow-y-auto whitespace-pre-wrap">{job.description}</div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
